import { Flex, Space } from 'antd';
import { Amplify, ResourcesConfig } from 'aws-amplify';
import {
  confirmSignIn,
  ConfirmSignInOutput,
  fetchAuthSession,
  getCurrentUser,
  GetCurrentUserOutput,
  signIn,
  SignInOutput
} from 'aws-amplify/auth';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LangugeSelect from 'src/components/LangugeSelect';
import LoginForm from 'src/components/loginForm';
import SmsForm from 'src/components/smsForm';
import {
  APP_ROUTES,
  FORM_TYPE,
  MEDIA_QUERIES,
  REACT_APP_HOSPITAL_CODE,
  USER_ACTIVITY_EVENT,
  USER_ACTIVITY_TYPE
} from 'src/enums';
import useWindowDimensions from 'src/hooks/useWindowDimensions';
import { ApiService } from 'src/services/api';
import IdleTimer from 'src/services/logoutTimer/IdleTimer';
import {
  layoutActions,
  useDispatch,
  userActions,
  userSelectors,
  useSelector
} from 'src/store';
import { useTheme } from 'src/styles/theme';
import { createLog, getErrorMessage, translateError } from 'src/utils';

import classes from './style/Login.module.css';
const AWS_COGNITO_APP_CLIENT_ID = process.env
  .REACT_APP_AWS_COGNITO_APP_CLIENT_ID as string;
const AWS_COGNITO_USER_POOL_ID = process.env
  .REACT_APP_AWS_COGNITO_USER_POOL_ID as string;
// OLD CODE OF AWS AMPLIFY 4
// Amplify.configure({
//   Auth: {
//     mandatorySignId: true,
//     region: process.env.REACT_APP_AWS_COGNITO_REGION,
//     userPoolId: process.env.REACT_APP_AWS_COGNITO_USER_POOL_ID,
//     userPoolWebClientId: process.env.REACT_APP_AWS_COGNITO_APP_CLIENT_ID
//   }
// });

// NEW CODE AWS AMPLIFY 6
const config: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolClientId: AWS_COGNITO_APP_CLIENT_ID,
      userPoolId: AWS_COGNITO_USER_POOL_ID
    }
  }
};
Amplify.configure(config);

const Login: React.FunctionComponent = () => {
  // TOOLS
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();
  const theme = useTheme();
  // STATE
  const [smsState, setSmsState] = React.useState<{
    loader: boolean;
    error: null | string;
  }>({ loader: false, error: null });
  const [signinState, setSigninState] = React.useState<{
    loader: boolean;
    error: null | string;
  }>({ loader: false, error: null });
  const [isModalVisible, setModalOpen] = React.useState(false);
  const [formType, setFormType] = React.useState(FORM_TYPE.LOGIN);

  // SELECTORS
  const hospital = useSelector(userSelectors.selectUserHospital);

  const getHospital = async () => {
    // get hospital
    const hospitalResult = await ApiService.fetchHospitals();
    if (REACT_APP_HOSPITAL_CODE) {
      const hospital = hospitalResult.data.find(
        (h: { code: string; logo: string }) =>
          h.code === REACT_APP_HOSPITAL_CODE
      );

      // save data to send to the sms route after this step
      dispatch(
        userActions.setUserHospital({
          hospital
        })
      );
    }
  };

  React.useEffect(() => {
    getHospital();
  }, []);

  // const originUrl = getValueFromUrlQuery(ORIGIN_URL_KEY, APP_ROUTES.PATIENTS_LIST);

  const onSubmit = async (values: { userId: string; phoneNumber: string }) => {
    try {
      if (!REACT_APP_HOSPITAL_CODE) {
        console.log('error in onSubmit login screen : HOSPITAL_CODE_NOT_FOUND');
        return;
      }
      const { userId, phoneNumber } = values;
      setSigninState({ loader: true, error: null });
      const response = await ApiService.preAuth({
        id: userId,
        phone_number: phoneNumber,
        hospitalCode: REACT_APP_HOSPITAL_CODE
      });
      console.log('response', response);
      const signInResponse: SignInOutput = await signIn({
        username: userId,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP'
        }
      });
      const { nextStep } = signInResponse;
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        // navigate to sms screen
        setFormType(FORM_TYPE.SMS);
      }

      setSigninState({ loader: false, error: null });
    } catch (error) {
      console.log('error in _signIn', error);
      setSigninState({
        loader: false,
        error: translateError(getErrorMessage(error), t)
      });
    }
  };

  const setApiToken = (jwtToken: string) => {
    ApiService.setAuthorization(jwtToken);
  };

  const onSubmitSms = async (values: { code: string }) => {
    const { code } = values;
    try {
      setSmsState({ loader: true, error: null });

      const respond: ConfirmSignInOutput = await confirmSignIn({
        challengeResponse: code
      });
      const { isSignedIn } = respond;

      // when the sms code is wrong
      if (!isSignedIn) {
        throw new Error('ERR002');
      }
      const { username, userId, signInDetails }: GetCurrentUserOutput =
        await getCurrentUser();
      console.log(`The id: ${username}`);
      console.log(`The sub: ${userId}`);
      console.log(`The signInDetails:`);
      console.log(signInDetails);
      const {
        // accessToken,
        idToken
      } = (await fetchAuthSession()).tokens ?? {};
      if (!idToken) {
        return;
      }
      const _idToken = idToken?.toString();
      // const _accessToken = accessToken?.toString();
      const payload = idToken?.payload;
      setApiToken(_idToken);
      const id = payload['custom:idnum'];
      if (!payload || !id) {
        alert('cant get id');
        throw new Error('cant get id');
      }

      if (!REACT_APP_HOSPITAL_CODE) {
        console.log('error in sms screen: HOSPITAL_CODE_NOT_FOUND');
        return;
      }
      // ************************* get user from namer *************************
      const res = await ApiService.getPatientDetailes({
        DeviceId: 'deviceId',
        Token: 'deviceToken',
        IsPatient: true
      });

      const { data } = res;
      if (data.group?.error) {
        console.log('error in getPatientDetailes==>1', data);
        throw new Error(data.group?.error);
      }
      if (data.ErrorCode) {
        console.log('error in getPatientDetailes==>2', data);
        throw new Error(data.ErrorCode);
      }
      const user = res.data;
      // SET LOGOUT TIMER
      IdleTimer.onAuth();
      dispatch(
        userActions.setUser({
          value: user
        })
      );
      // open download app modal after login
      dispatch(layoutActions.openDownloadAppModal());
      // create log
      createLog(
        user,
        USER_ACTIVITY_EVENT.LOGIN,
        USER_ACTIVITY_TYPE.SCREEN,
        location.pathname || ''
      );
      navigate(`${APP_ROUTES.HOME}`);

      setSmsState({ loader: false, error: null });
    } catch (error) {
      console.log('error in _sendSms', error);
      setSmsState({
        loader: false,
        error: translateError(getErrorMessage(error), t)
      });
    }
  };

  return (
    <>
      {width !== null && width < MEDIA_QUERIES.MOBILE && (
        <>
          <Flex justify="flex-end" className={classes.languageFlex}>
            <LangugeSelect />
          </Flex>
          <img src={'/assets/mobileImg.png'} className={classes.mobileImg} />
        </>
      )}
      <Flex className={classes.mainFlex}>
        <div className={classes.formContainer}>
          <Flex justify="space-between" className={classes.logoContainer}>
            <Space>
              <img
                className={`${classes.logo} `}
                src={hospital?.logo || '/assets/space_saver.png'}
                alt={t('accessibility.ministryOfHealthLogo')}
                title={t('accessibility.ministryOfHealthLogo')}
              />
              {/* <p className={`${theme.fonts.classNames.cardTitle}`}>
                {`${t('login.medicalCenter')} ${hospital?.name}`}{' '}
              </p> */}
            </Space>

            <LangugeSelect />
          </Flex>
          {width !== null && width <= MEDIA_QUERIES.MOBILE && (
            <Space
              direction="vertical"
              size="middle"
              className={classes.titleSpace}
            >
              <h1 className={`${theme.fonts.classNames.mobilePageTitle} `}>
                {t('login.title')}
              </h1>
              <h1 className={`${theme.fonts.classNames.mobileSubPageTitle} `}>
                {t('login.subtitle')}
              </h1>
            </Space>
          )}

          {formType === FORM_TYPE.LOGIN && (
            <Flex justify="center">
              <LoginForm
                onSubmit={onSubmit}
                isLoading={signinState.loader}
                errorMessage={signinState.error}
                setModalOpen={setModalOpen}
                isModalVisible={isModalVisible}
              />
            </Flex>
          )}
          {formType === FORM_TYPE.SMS && (
            <Flex justify="center">
              <SmsForm
                onSubmit={onSubmitSms}
                isLoading={smsState.loader}
                errorMessage={smsState.error}
              />
            </Flex>
          )}
        </div>

        <Flex className={classes.mainImageContainer}>
          <img src={'/assets/login.png'} className={classes.mainImg} />
        </Flex>
      </Flex>
    </>
  );
};

export default Login;
